import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { bannedAgentService, tenderService } from "../../services/api";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { usePermission } from "../../hooks/usePermission";

export const useViewPitches = (isStaffView = false) => {
  const { tenderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const initialTender = location.state?.tender;

  const [tender, setTender] = useState(initialTender);
  const [activeTab, setActiveTab] = useState("active");
  const [pitches, setPitches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTenderDetails, setShowTenderDetails] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPitch, setSelectedPitch] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  // What the club sees AFTER a successful accept: the success dialog, holding
  // the chat it may open. Null while there is nothing to celebrate.
  // `{ channel, agentName }` — see `handleConfirmAction`.
  const [acceptSuccess, setAcceptSuccess] = useState(null);
  // Club-side filter: when on, the pitches list only shows pitches the
  // agent submitted with the 'Free Player' toggle (= no transfer fee).
  // Threads through to the backend via `?free_agents_only=true`.
  const [freeAgentsOnly, setFreeAgentsOnly] = useState(false);
  const { user, roles, currentRole } = useSelector((state) => state.auth);
  const fetchedTenderRef = useRef(false);

  // Permission checks
  const canAcceptPitches = usePermission("accept_pitches");
  const canViewPitches = usePermission("view_pitches");
  // The silent ban is gated on `view_tenders` — not invented here but read off
  // the endpoint that will answer the click
  // (dashboard/agent_ban_views.py: ClubBannedAgentViewSet.required_permission
  // = 'view_tenders'), which is also the gate on the Banned Agents page and its
  // route. PART A line 48: "Access: every club member who has access to Club
  // Tenders — the same permission gates this menu."
  const canBanAgents = usePermission("view_tenders");

  const formatPosition = useCallback((pos) => {
    if (!pos) return "-";
    return pos
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, []);

  const fetchTenderData = useCallback(async () => {
    if (!tenderId) return;
    try {
      const response = await tenderService.getSingleTender(tenderId);
      if (response.success) {
        setTender(response.data);
      }
    } catch (error) {
      console.error("Error fetching tender data:", error);
    }
  }, [tenderId]);

  const fetchPitches = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await tenderService.getPitches(
        tenderId,
        activeTab,
        signal,
        { freeAgentsOnly },
      );
      if (response.success) {
        setPitches(response.data.results || []);
      } else {
        setError(response.error);
      }
    } catch (err) {
      if (err.name === "AbortError" || err.message === "canceled") return;
      setError("Failed to fetch pitches. Please try again.");
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [tenderId, activeTab, freeAgentsOnly]);

  useEffect(() => {
    if (!tenderId) {
      navigate(isStaffView ? "/staff/tenders" : "/club/tenders");
      return;
    }

    const controller = new AbortController();
    fetchPitches(controller.signal);

    return () => {
      controller.abort();
    };
  }, [tenderId, activeTab, freeAgentsOnly, fetchPitches, navigate, isStaffView]);

  useEffect(() => {
    if (tenderId && !fetchedTenderRef.current) {
      fetchTenderData();
      fetchedTenderRef.current = true;
    }
  }, [tenderId, fetchTenderData]);

  const handleAcceptReject = (pitch, action) => {
    setSelectedPitch(pitch);
    setConfirmAction(action);
    setShowConfirmModal(true);
  };

  // "Reopen & chat" — the club picks an expired pitch back up. PART A
  // (2026-07-31): "In the Expired tab the card gets the honest reopen button:
  // 'Reopen & chat' — it accepts and immediately opens the chat."
  //
  // Deliberately NOT its own action: reopening IS accepting, so it goes through
  // the very same confirmation, the same PATCH to 'accepted', the same chat
  // channel and the same redirect. The backend agrees — views.update_status
  // routes the reopen through the ordinary accept branch and only logs that it
  // came from expired. All that differs is the wording, and that is derived
  // once in `confirmDialog` below.
  const handleReopen = (pitch) => {
    setSelectedPitch(pitch);
    setConfirmAction("reopen");
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedPitch || !confirmAction) return;
    const pitchId = selectedPitch.id;
    const isReopen = confirmAction === "reopen";
    // Reopen sends 'accepted' because it IS an accept — one status, one path.
    const statusToSend =
      confirmAction === "accept" || isReopen ? "accepted" : "rejected";

    setShowConfirmModal(false);
    setLoading(true);

    try {
      const response = await tenderService.updatePitchStatus(pitchId, statusToSend);
      if (response.success) {
        // Refreshes the tab counters (the pitch just left Expired for
        // Accepted). The counts live on the tender payload and are rendered by
        // ViewPitchesTabs — nothing recounts them here.
        await fetchTenderData();
        // The list is reloaded on EVERY outcome now, accept included. It used
        // to be skipped in the accept branch because the page navigated away a
        // moment later; the page stays, so the card has to move to the tab it
        // now belongs in instead of sitting in "Pitches received" as accepted.
        const controller = new AbortController();
        await fetchPitches(controller.signal);
        if (statusToSend === "accepted") {
          // NO automatic jump to the inbox any more. A club usually has SEVERAL
          // pitches to decide on one request, and being thrown out of the list
          // after the first one costs it its place (Marin, 2026-08-02). What
          // replaces the redirect is the choice the design always specified
          // (handoff, open point 9): a success dialog with "Go to Inbox" and
          // "Later".
          //
          // The channel comes from the PATCH response, which only the accepted
          // branch fills. When it is absent the card's own derivation is used —
          // `pitch-<id>` / "messaging", the exact pair the "Go to Inbox" button
          // on an accepted card already navigates with (ViewPitchesList) — so
          // the dialog is never a dead end.
          const channel =
            response.data?.results?.channel || response.results?.channel || null;
          setAcceptSuccess({
            channel: {
              channelId: channel?.channel_id || `pitch-${pitchId}`,
              channelType: channel?.channel_type || "messaging",
            },
            agentName: selectedPitch?.applied_by_agent?.name || null,
          });
        }
      } else {
        // `response.error` is the backend's own `message` (api.js:2243), not a
        // status code — guard the string so a response without one cannot throw
        // its way into the generic catch below.
        const backendMessage = response.error || "";
        if (backendMessage.includes("already in status: accepted")) {
          // Somebody else accepted this pitch first. This used to redirect to a
          // bare /inbox — a jump on a REFUSAL, and to the inbox root with no
          // channel, so the club landed nowhere in particular and lost its
          // place in the list for it. It is told what happened, in the
          // backend's own words, and the refreshed card carries its own
          // "Go to Inbox" button, which knows the right channel.
          toast.error(backendMessage);
          await fetchTenderData();
          const refreshed = new AbortController();
          await fetchPitches(refreshed.signal);
          return;
        }
        if (isReopen) {
          // Both refusals the reopen can earn are already spelled out honestly
          // by dashboard/pitch_lifecycle.py, and both are shown verbatim rather
          // than flattened into "something went wrong":
          //   400 "The 14-day window to pick up this expired pitch closed on
          //        {date}. It can no longer be accepted."  (window over)
          //   409 "This pitch is already {status} and cannot be decided
          //        again."                                 (the auto-reject
          //        sweep, or another colleague, got there first)
          // Then the list is refetched, so the card the club just clicked shows
          // what actually happened instead of staying reopenable.
          toast.error(
            backendMessage ||
              "This pitch could not be picked up. Please refresh and try again.",
          );
          await fetchTenderData();
          const controller = new AbortController();
          await fetchPitches(controller.signal);
          return;
        }
        setError(backendMessage || `Failed to ${confirmAction} pitch`);
      }
    } catch (err) {
      console.error(`Error ${confirmAction}ing pitch:`, err);
      if (isReopen) {
        toast.error("Failed to reopen this pitch. Please try again.");
      } else {
        setError(`Failed to ${confirmAction} pitch. Please try again.`);
      }
    } finally {
      setLoading(false);
      setSelectedPitch(null);
      setConfirmAction(null);
    }
  };

  // The copy of the accept / reject / reopen confirmation, in ONE place. The
  // club, staff and coach pitch pages render an identical ConfirmationModal;
  // adding the third variant to three near-identical ternaries is how the
  // wording of one of them quietly drifts.
  const confirmDialog = (() => {
    const pitchedName =
      tender?.tender_category === "staff"
        ? selectedPitch?.staff_name
        : selectedPitch?.player?.name;

    if (confirmAction === "reopen") {
      return {
        title: "Reopen Pitch",
        message: `Pick this expired pitch from ${pitchedName} back up? It accepts the pitch and opens the chat with the agent.`,
        confirmText: "Reopen & chat",
        confirmButtonClass: "bg-blue-500 hover:bg-blue-600",
      };
    }
    const isAccept = confirmAction === "accept";
    return {
      title: `${isAccept ? "Accept" : "Reject"} Pitch`,
      message: `Are you sure you want to ${confirmAction} this pitch from ${pitchedName}?`,
      confirmText: isAccept ? "Accept" : "Reject",
      confirmButtonClass: isAccept
        ? "bg-blue-500 hover:bg-blue-600"
        : "bg-red-500 hover:bg-red-600",
    };
  })();

  // The success dialog that took the place of the automatic redirect.
  //
  // Copy and buttons are the design's (prototype `View Pitches V2.dc.html`, the
  // `accepted-ok` dialog): a title, two sentences of equal weight, "Later" on
  // the left and "Go to Inbox" on the right, centred — the v2 ConfirmationModal
  // the rest of this surface already uses, not a second dialog component.
  //
  // A REOPEN gets the very same dialog on purpose. It sends the same
  // 'accepted', creates the same chat and leaves the pitch in the same place;
  // the prototype routes both outcomes into this one dialog, and inventing a
  // second closing screen would tell the club that something different
  // happened when nothing did.
  const acceptSuccessDialog = acceptSuccess
    ? {
        title: "You have successfully accepted this pitch.",
        lines: [
          acceptSuccess.agentName
            ? `You can now chat with ${acceptSuccess.agentName} in your Inbox to finalise the deal.`
            : "You can now chat with the agent in your Inbox to finalise the deal.",
          "If you wish to continue later, you can access this chat anytime from the Inbox tab.",
        ],
        confirmText: "Go to Inbox",
        cancelText: "Later",
        confirmButtonClass: "bg-[#2183FE] hover:bg-[#1668D9]",
      }
    : null;

  // "Later" — the dialog closes and NOTHING else happens. The club is still on
  // its list, on the tab it was on, with the next pitch to decide.
  const dismissAcceptSuccess = () => setAcceptSuccess(null);

  const goToAcceptedChat = () => {
    const channel = acceptSuccess?.channel;
    setAcceptSuccess(null);
    if (!channel) return;
    navigate("/inbox", {
      state: {
        channelId: channel.channelId,
        channelType: channel.channelType,
      },
    });
  };

  // "Scouting" — the club signals "we're looking at him, we need time".
  // Deliberately NOT behind the ConfirmationModal that guards accept/reject:
  // it decides nothing and takes nothing away (PART A lists it beside "do
  // nothing", not beside the two decisions). The guard that matters — at most
  // three clicks — lives in the backend (pitch_lifecycle.PITCH_SCOUTING_MAX),
  // so a stale card that still shows the button gets an honest refusal instead
  // of a fourth extension.
  const handleScouting = async (pitch) => {
    if (!pitch?.id) return;
    setLoading(true);
    try {
      const response = await tenderService.updatePitchStatus(pitch.id, "scouting");
      if (response.success) {
        // Says where the pitch went: the first click moves it out of
        // "Pitches received", and a card vanishing without a word reads like
        // the click destroyed something.
        toast.success(
          // No em-dash: the V2 copy rules use a full stop, a comma or a middle
          // dot as separators (build brief, "Rules").
          "Sent to scouting. You have 3 more days to decide. The pitch is now in the Scouting tab.",
        );
        await fetchTenderData();
        const controller = new AbortController();
        await fetchPitches(controller.signal);
      } else {
        // The backend message carries the reason (limit reached, pitch already
        // decided elsewhere) — surface it rather than a generic failure.
        toast.error(response.error || "Failed to send this pitch to scouting.");
      }
    } catch (err) {
      console.error("Error sending pitch to scouting:", err);
      toast.error("Failed to send this pitch to scouting. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // REMOVED with the V2 facade: `handleExportToExcel` and its `exportingData`
  // flag. The "Export to Excel" button was never meant to be in this area
  // (Marin, View Pitches V2 build brief: "the button exists in the old code;
  // Marin ruled it was never intended. Remove it and its handler"), and the
  // handler was the only reason this hook pulled in `xlsx` and fired six extra
  // list requests (one per status) on a single click. Both are gone rather than
  // hidden, together with the two dead field reads it carried
  // (`applied_by_agent.email`, which AgentMinimalSerializer never sends, and
  // `player.current_club`, which PlayerMinimalSerializer never sends either).
  //
  // The REAL, silent ban behind the card's three-dot menu (PART A, "The real
  // ban"). It replaces a dummy that called nothing at all and closed itself.
  //
  // `pitch_id` alone is the whole payload the card needs: the endpoint reads
  // BOTH the agent and the request off the pitch, scoped to the caller's club
  // (agent_ban_views.py `_resolve_target`), so the frontend never has to
  // assemble an agent id — and cannot assemble a wrong one.
  //
  // Deliberately NO refetch afterwards, unlike accept / reject / scouting.
  // PART A line 47: "existing pitches of a banned agent (any state) continue
  // their normal lifecycle untouched". The list and the tab counters are
  // therefore CORRECT as they stand — the banned agent's pitches stay exactly
  // where they are. A refetch here would suggest something moved, and the
  // counters (WP2's numbers, served on the tender payload) have nothing to
  // recount. Nothing notifies the agent either: there is no notify() anywhere
  // in the ban endpoints, and none is added here.
  //
  // Returns true/false so the menu knows whether to close its dialog.
  const handleBanAgent = async (pitch, scope) => {
    if (!pitch?.id || !scope) return false;
    const agentName = pitch.applied_by_agent?.name || "This agent";
    const response = await bannedAgentService.ban({ scope, pitch_id: pitch.id });
    if (response.success) {
      toast.success(
        scope === "transfer_window"
          ? `${agentName} is banned for this transfer window. They are not notified.`
          : `${agentName} is banned from this request. They are not notified.`,
      );
      return true;
    }
    // The backend's own message carries the reason (not a club member, pitch
    // without an author, request gone) — shown as it stands rather than
    // flattened, and never as a success.
    toast.error(response.error || "Failed to ban this agent.");
    return false;
  };

  return {
    tender,
    activeTab,
    setActiveTab,
    pitches,
    loading,
    error,
    showTenderDetails,
    setShowTenderDetails,
    showConfirmModal,
    setShowConfirmModal,
    selectedPitch,
    confirmAction,
    confirmDialog,
    acceptSuccess,
    acceptSuccessDialog,
    dismissAcceptSuccess,
    goToAcceptedChat,
    canAcceptPitches,
    canViewPitches,
    canBanAgents,
    formatPosition,
    handleAcceptReject,
    handleConfirmAction,
    handleReopen,
    handleScouting,
    handleBanAgent,
    navigate,
    freeAgentsOnly,
    setFreeAgentsOnly,
  };
};

